import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  useTheme,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import Logout from "@mui/icons-material/Logout";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ThemeToggle from "./ThemeToggle";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

const hiAnimation = keyframes`
  0% { transform: translateY(10px); opacity: 0; }
  50% { transform: translateY(0); opacity: 1; }
  100% { transform: translateY(10px); opacity: 0; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-4px); }
  100% { transform: translateY(0px); }
`;

const wave = keyframes`
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
`;

function WaveformIcon({ size = 24, color = "white" }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        gap: "2px",
        height: size,
      }}
      aria-hidden="true"
    >
      {[0, 0.1, 0.2, 0.3, 0.4].map((delay, i) => (
        <Box
          key={i}
          sx={{
            width: "3px",
            height: "100%",
            bgcolor: color,
            borderRadius: "2px",
            transformOrigin: "bottom",
            animation: `${wave} 1s ${delay}s infinite ease-in-out`,
          }}
        />
      ))}
    </Box>
  );
}

const Header = ({ darkMode, setDarkMode, onNewChat, sidebarWidth }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [showGreeting, setShowGreeting] = useState(false);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { logout, user } = useContext(AuthContext);

  const handleAvatarClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    setShowGreeting(true);
    const timer = setTimeout(() => setShowGreeting(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const bgGradient =    darkMode
            ? "linear-gradient(145deg, #1e293b, #111827)"
            : "linear-gradient(145deg, #f0f4f8, #e2e8f0)";
  // darkMode
  //   ? "linear-gradient(135deg, #1e1e2f, #2d2d44, #1e1e2f)"
  //   : "linear-gradient(135deg, #ffffff, #f3f4f6, #ffffff)";

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        ml: `calc(${sidebarWidth}px + 2px)`,
        width: `calc(100% - ${sidebarWidth}px - 2px)`,
        zIndex: 1200,
        transition: "all 0.3s ease",
        background: bgGradient,
        color: darkMode ? "#f1f5f9" : "#1e3a8a",
        backdropFilter: "blur(16px)",
        borderBottom: darkMode
          ? "1px solid rgba(255,255,255,0.05)"
          : "1px solid rgba(0,0,0,0.06)",
        borderRadius: "0 0 14px 14px",
        px: { xs: 1, sm: 2 },
      }}
      component="header"
      role="banner"
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: 72,
          display: "flex",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2 },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.8, sm: 1.2 },
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {showGreeting ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.8, sm: 1.2 },
                animation: `${hiAnimation} 2s ease-in-out`,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <AutoAwesomeIcon
                sx={{
                  color: darkMode ? "#93c5fd" : "#2563eb",
                  fontSize: { xs: 24, sm: 30 },
                  animation: `${pulse} 1.5s infinite`,
                }}
                aria-hidden="true"
              />
              <Typography
                variant={isSmDown ? "subtitle1" : "h6"}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  fontSize: isSmDown ? "1rem" : "1.25rem",
                  background: darkMode
                    ? "linear-gradient(90deg,#93c5fd,#c7d2fe)"
                    : "linear-gradient(90deg,#2563eb,#9333ea)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Hiii!
              </Typography>
            </Box>
          ) : (
            <>
              <IconButton
                aria-label="Waveform icon"
                size={isSmDown ? "small" : "medium"}
                sx={{ p: 0 }}
              >
                <WaveformIcon
                  color={darkMode ? "#93c5fd" : "#2563eb"}
                  size={isSmDown ? 20 : 24}
                />
              </IconButton>

              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
              >
                <Typography
                  variant={isSmDown ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    fontSize: isSmDown ? "1rem" : "1.25rem",
                    background: darkMode
                      ? "linear-gradient(90deg,#93c5fd,#c7d2fe)"
                      : "linear-gradient(90deg,#2563eb,#9333ea)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Tatva AI
                </Typography>

                <Box
                  component="span"
                  sx={{
                    fontSize: "0.55rem",
                    fontWeight: 700,
                    color: darkMode ? "#fcd34d" : "#fff",
                    bgcolor: darkMode ? "#334155" : "#2563eb",
                    borderRadius: "3px",
                    px: 0.5,
                    py: 0.25,
                    ml: -0.5,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    animation: "float 2s ease-in-out infinite",
                    "@keyframes float": {
                      "0%,100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-9px)" },
                    },
                  }}
                >
                  Beta
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            flexWrap: "nowrap",
            minWidth: 0,
          }}
        >
          <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />
          <Button
            variant="contained"
            onClick={onNewChat}
            startIcon={
              <ChatBubbleOutlineIcon
                sx={{
                  fontSize: isSmDown ? 18 : 20,
                  animation: `${float} 2s ease-in-out infinite`,
                }}
                aria-hidden="true"
              />
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: { xs: 1.5, sm: 2.5 },
              py: { xs: 0.5, sm: 1 },
              fontSize: isSmDown ? "0.7rem" : "0.8rem",
              background: darkMode
                ? "linear-gradient(90deg,#4f46e5,#7c3aed)"
                : "linear-gradient(90deg,#2563eb,#9333ea)",
              "&:hover": { transform: "translateY(-1px)" },
              minWidth: isSmDown ? 90 : 120,
            }}
            aria-label="Start a new chat"
          >
            New Chat
          </Button>
          <Avatar
            onClick={handleAvatarClick}
            sx={{
              bgcolor: darkMode ? "#6366f1" : "#2563eb",
              fontWeight: 600,
              width: isSmDown ? 32 : 38,
              height: isSmDown ? 32 : 38,
              fontSize: isSmDown ? "0.75rem" : "0.9rem",
              cursor: "pointer",
            }}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            aria-label="User account menu"
          >
            {user?.username?.[0]?.toUpperCase() || "U"}
          </Avatar>
          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{
              elevation: 3,
              sx: {
                borderRadius: "10px",
                mt: 1.5,
                minWidth: 180,
                background: darkMode ? "#1f2937" : "#fff",
                color: darkMode ? "#f1f5f9" : "#111827",
              },
            }}
            MenuListProps={{
              "aria-labelledby": "account-button",
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                navigate("/profile");
              }}
            >
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            {/* <MenuItem
              onClick={() => {
                handleClose();
                navigate("/settings");
              }}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem> */}
            <Divider />
            <MenuItem
              onClick={() => {
                logout();
                navigate("/login");
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
